import './ModelHighlights.scss';
import { Fragment, ReactNode, useMemo, useRef, useState } from 'react';
import { Area, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Brand0, Brand100, Brand30, Brand50, Brand60, Neutral600 } from '@src/utils/colors';
import { Badge } from '@Components/Badge';
import {
  ChartTooltipV2,
  KeyShapeOptions,
  PayloadItem
} from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { Appearance, Background, Design, Intent, Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';
import { DashboardData, HierarchyLevel, HierarchyLevelConst } from '../dtc-commerce-mmm-types';
import { formatFieldName } from '../hooks/useChannelAggregation';
import {
  formatCurrency,
  formatDollarsExact,
  formatWholeNumber
} from '../utils/dtc-commerce-mmm-format';
import { ChannelIcon } from './ChannelIcon';
import { IncrementalityBeaker } from './IncrementalityBeaker';
import { IncrementalityNote } from './IncrementalityNote';
import { MediaTypeBadge, MediaTypeBadgeVariant } from './MediaTypeBadge';
import { InsightFeatureMeta, TagTone, TagToneType } from './channel-insights-constants';
import {
  ComparisonMode,
  ComparisonModeType,
  HeadlineTile,
  HeadlineTileKind,
  HeadlineTileKindType,
  HighlightCard,
  HighlightChip,
  TilePeriod,
  TilePeriodType,
  TilePoint,
  TOOLTIP_FLIP_EDGE_PX
} from './model-highlights-constants';
import { buildHeadlineTiles, formatRangeLabel } from './model-highlights-tiles-utils';
import { buildModelHighlights } from './model-highlights-utils';
import { buildDisplayName, buildGroupKey } from './reallocation-v4-utils';

interface ModelHighlightsProps {
  data: DashboardData;
  hierarchyLevel: HierarchyLevel;
  onChannelClick: (groupKey: string, focusedFeatureName?: string) => void;
}

const TAG_BADGE_DESIGNS = {
  [TagTone.POSITIVE]: Intent.SUCCESS,
  [TagTone.NEGATIVE]: Intent.ERROR,
  [TagTone.CAUTION]: Intent.WARNING,
  [TagTone.NEUTRAL]: Design.NEUTRAL,
  [TagTone.BRAND]: Design.BRAND
} as const satisfies Record<TagToneType, unknown>;

// The labels over the tooltip's two panels, named for what each chart point is.
const TOOLTIP_PANEL_LABELS: Record<TilePeriodType, readonly [string, string]> = {
  [TilePeriod.WEEK]: ['Primary Week', 'Comparison Week'],
  [TilePeriod.MONTH]: ['Primary Month', 'Comparison Month']
};

const COMPARISON_NOTES: Record<ComparisonModeType, string> = {
  [ComparisonMode.SAME_WEEKS]: ' Changes are compared with the same weeks a year earlier.',
  [ComparisonMode.LATEST_YEAR]: ' Changes compare your latest year with the one before.',
  [ComparisonMode.FIRST_VS_LATEST]:
    ' Changes compare your latest year with the same months at the start of your dates.',
  [ComparisonMode.NONE]: ' The highlight cards need 8 weeks or more to compare with a year earlier.'
};

// Four headline tiles, then up to six highlight cards, for the picked dates. Always actualized:
// the Actualized/Direct choice belongs to Channel Performance alone.
export const ModelHighlights = ({ data, hierarchyLevel, onChannelClick }: ModelHighlightsProps) => {
  const { dateRange } = useDtcCommerceMmmData();
  const isRevenue = data.modelInfo.kpiType === 'revenue';
  const mape = data.modelInfo.testMape;
  const cptbWindow = data.actualized.cptbT12M;
  const avpWindow = data.actualVsPredictedT12M;
  const { cptb, roas, saturation } = data.actualized;

  const metaByName = useMemo(() => {
    const map = new Map<string, InsightFeatureMeta>();
    roas.forEach(row => {
      map.set(row.name, { row, displayName: buildDisplayName(row, formatFieldName) });
    });
    return map;
  }, [roas]);

  const tiles = useMemo(
    () =>
      buildHeadlineTiles({
        cptbWindow,
        cptbHistory: cptb,
        avpWindow,
        isRevenue,
        mape,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }),
    [cptbWindow, cptb, avpWindow, isRevenue, mape, dateRange.startDate, dateRange.endDate]
  );

  const highlights = useMemo(
    () =>
      buildModelHighlights({
        cptb,
        roas,
        saturation,
        actualVsPredicted: data.actualVsPredicted,
        startDate: data.t12mStartDate,
        endDate: data.t12mEndDate,
        isRevenue,
        displayNameFor: name => metaByName.get(name)?.displayName ?? name
      }),
    [
      cptb,
      roas,
      saturation,
      data.actualVsPredicted,
      data.t12mStartDate,
      data.t12mEndDate,
      isRevenue,
      metaByName
    ]
  );

  const handleFeatureClick = (name: string) => {
    const meta = metaByName.get(name);
    if (!meta) return;
    onChannelClick(buildGroupKey(meta.row, hierarchyLevel), name);
  };

  // Names the first and last weeks of data inside the picked dates, not the raw picker dates.
  const rangeLabel = useMemo(() => {
    const weeks = cptbWindow.map(r => r.date).sort();
    if (weeks.length === 0) return formatRangeLabel(data.t12mStartDate, data.t12mEndDate);
    return formatRangeLabel(weeks[0], weeks[weeks.length - 1]);
  }, [cptbWindow, data.t12mStartDate, data.t12mEndDate]);

  return (
    <WidgetContainer
      background={Background.DARK}
      className='model-highlights-widget'
      collapsible
      header='Model Highlights'
      subHeader={
        <>
          What stands out {rangeLabel && `from ${rangeLabel} `}· Based on actualized model results
          <InfoTooltip className='dtc-commerce-mmm-subheader-info-icon' size={Size.SM} inline>
            These highlights cover the dates you pick above, the same weeks as the totals at the
            top.
            {COMPARISON_NOTES[highlights.comparison]} Seasonal patterns come from your full model
            history. Suggestions reflect your most recent 3 months of results and appear only when
            your dates run up to the latest results. Demand-capture channels get descriptions only.
          </InfoTooltip>
        </>
      }
    >
      <div className='model-highlights'>
        <div className='model-highlights-tiles'>
          {tiles.map(tile => (
            <HeadlineTileView key={tile.kind} tile={tile} isRevenue={isRevenue} />
          ))}
        </div>
        <HighlightCards
          portfolio={highlights.portfolio}
          channels={highlights.channels}
          tooShort={highlights.tooShort}
          metaByName={metaByName}
          onFeatureClick={handleFeatureClick}
        />
      </div>
      <div className='dtc-commerce-mmm-incrementality-footer'>
        <IncrementalityNote />
      </div>
    </WidgetContainer>
  );
};

interface TileSeries {
  metricLabel: string;
  formatValue: (v: number) => string;
}

// Tooltip row names and number formats per tile, matching the Executive Summary's sparks.
const tileSeries = (kind: HeadlineTileKindType, isRevenue: boolean): TileSeries => {
  if (kind === HeadlineTileKind.RETURN) {
    return { metricLabel: isRevenue ? 'ROAS' : 'CPA', formatValue: formatDollarsExact };
  }
  if (kind === HeadlineTileKind.SPEND) return { metricLabel: 'Spend', formatValue: formatCurrency };
  return {
    metricLabel: isRevenue ? 'Revenue' : 'Conversions',
    formatValue: isRevenue ? formatCurrency : formatWholeNumber
  };
};

interface HeadlineTileViewProps {
  tile: HeadlineTile;
  isRevenue: boolean;
}
// Five rows (top / value / chart / headline / chips) that line up across the tiles via subgrid.
// Hovering the tile reveals the year-earlier line, as on the cross-channel spark charts.
const HeadlineTileView = ({ tile, isRevenue }: HeadlineTileViewProps) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className='model-highlights-tile'
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='model-highlights-tile-top'>
        <span className='model-highlights-tile-title'>{tile.title}</span>
        <span className='model-highlights-tile-context'>
          <span className='model-highlights-tile-context-text'>{tile.context}</span>
          {tile.kind === HeadlineTileKind.ACCURACY && <MapeInfoTooltip />}
        </span>
      </div>
      <div className='model-highlights-tile-value-row'>
        <span className='model-highlights-tile-value'>{tile.value}</span>
        <span className='model-highlights-tile-unit'>{tile.unit}</span>
      </div>
      <TileChart
        points={tile.points}
        kind={tile.kind}
        period={tile.period}
        series={tileSeries(tile.kind, isRevenue)}
        hovered={hovered}
      />
      <div className='model-highlights-tile-headline'>{tile.headline}</div>
      <div className='model-highlights-tile-chips'>
        {tile.chip && <ChipBadge chip={tile.chip} />}
      </div>
    </div>
  );
};

const MapeInfoTooltip = () => (
  <InfoTooltip className='dtc-commerce-mmm-subheader-info-icon' size={Size.SM}>
    MAPE (Mean Absolute Percentage Error) measures how far the model&apos;s predictions were from
    actual results on data the model didn&apos;t see during training — lower is better. It&apos;s a
    property of the model itself, so it doesn&apos;t change with the dashboard&apos;s date range.
  </InfoTooltip>
);

interface TileChartProps {
  points: TilePoint[];
  kind: HeadlineTileKindType;
  period: TilePeriodType;
  series: TileSeries;
  hovered: boolean;
}
// Full-bleed area chart with no axes. The accuracy tile adds the dashed predicted line; the others
// add the dashed year-earlier line while the tile is hovered.
const TileChart = ({ points, kind, period, series, hovered }: TileChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [tooltipFlipped, setTooltipFlipped] = useState(false);
  const showsPredicted = kind === HeadlineTileKind.ACCURACY;
  // Only drawn on hover, so the resting chart stays scaled to the picked dates alone.
  const showsYearEarlier = hovered && points.some(p => p.yearEarlier !== undefined);
  const gradientId = `model-highlights-spark-${kind}`;
  return (
    <div className='model-highlights-tile-chart' ref={chartRef}>
      {showsPredicted && <AccuracyLegend />}
      <ResponsiveContainer width='100%' height='100%'>
        <ComposedChart
          data={points}
          // Clear air between the big number and the lines, plus room for the accuracy legend.
          margin={{ top: showsPredicted ? 32 : 20, right: 0, bottom: 0, left: 0 }}
          onMouseMove={(_state, e) => {
            // The tooltip may leave the tile sideways, so near the right edge it flips to the
            // cursor's left. Cross-channel measures to the viewport; here the edge is the row of
            // tiles, because the collapsible widget around it clips anything past its sides.
            if (!e || typeof e.clientX !== 'number') return;
            const row = chartRef.current?.closest('.model-highlights-tiles');
            const rightEdge = row?.getBoundingClientRect().right ?? window.innerWidth;
            setTooltipFlipped(rightEdge - e.clientX < TOOLTIP_FLIP_EDGE_PX);
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='1%' stopColor={Brand30} />
              <stop offset='95%' stopColor={Brand0} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            content={<TilePointTooltip period={period} series={series} />}
            allowEscapeViewBox={{ x: true }}
            reverseDirection={{ x: tooltipFlipped, y: false }}
          />
          <XAxis hide dataKey='key' />
          <YAxis hide />
          <Area
            type='linear'
            dataKey='value'
            stroke={Brand50}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            fillOpacity={1}
            activeDot={false}
            isAnimationActive={false}
          />
          {showsPredicted && (
            <Line
              type='linear'
              dataKey='predicted'
              stroke={Neutral600}
              strokeWidth={2}
              strokeDasharray='4'
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          {showsYearEarlier && (
            <Line
              type='linear'
              dataKey='yearEarlier'
              stroke={Brand100}
              strokeWidth={2}
              strokeDasharray='4'
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const AccuracyLegend = () => (
  <div className='model-highlights-tile-legend'>
    <span className='model-highlights-tile-legend-item'>
      <span className='model-highlights-tile-legend-swatch' />
      Actual
    </span>
    <span className='model-highlights-tile-legend-item'>
      <span className='model-highlights-tile-legend-swatch predicted' />
      Predicted
    </span>
  </div>
);

interface TilePointTooltipProps {
  active?: boolean;
  payload?: { payload?: TilePoint }[];
  period: TilePeriodType;
  series: TileSeries;
}
const TilePointTooltip = ({ active, payload, period, series }: TilePointTooltipProps) => {
  if (!active || !payload?.length || !payload[0].payload) return null;
  const point = payload[0].payload;
  const { metricLabel, formatValue } = series;
  if (point.yearEarlier !== undefined) {
    // The picked dates beside the same weeks a year earlier, laid out as on cross-channel.
    return (
      <ChartTooltipV2
        headers={[point.label, point.yearEarlierLabel]}
        headerSuperLabels={TOOLTIP_PANEL_LABELS[period]}
        items={[{ label: metricLabel, value: formatValue(point.value), color: Brand60 }]}
        comparisonItems={[
          { label: metricLabel, value: formatValue(point.yearEarlier), color: Brand100 }
        ]}
      />
    );
  }
  const actual: PayloadItem = {
    label: point.predicted === undefined ? metricLabel : `Actual ${metricLabel}`,
    value: formatValue(point.value),
    color: Brand60,
    keyShape: KeyShapeOptions.LINE
  };
  const items: PayloadItem[] =
    point.predicted === undefined
      ? [actual]
      : [
          actual,
          {
            label: `Predicted ${metricLabel}`,
            value: formatValue(point.predicted),
            color: Neutral600,
            keyShape: KeyShapeOptions.DASHED_LINE
          }
        ];
  return <ChartTooltipV2 headers={[point.label]} items={items} />;
};

interface HighlightCardsProps {
  portfolio: HighlightCard[];
  channels: HighlightCard[];
  tooShort: boolean;
  metaByName: Map<string, InsightFeatureMeta>;
  onFeatureClick: (name: string) => void;
}
// Portfolio cards lead, channel cards fill the rest of the grid.
const HighlightCards = ({
  portfolio,
  channels,
  tooShort,
  metaByName,
  onFeatureClick
}: HighlightCardsProps) => {
  if (tooShort) {
    return <div className='model-highlights-note'>Pick at least 4 weeks to see highlights.</div>;
  }
  const cards = [...portfolio, ...channels];
  if (cards.length === 0) {
    return <div className='model-highlights-note'>Nothing stands out for these dates.</div>;
  }
  return (
    <div className='model-highlights-cards'>
      {cards.map(card => {
        const { featureName } = card;
        return (
          <HighlightCardView
            key={card.id}
            card={card}
            metaByName={metaByName}
            meta={featureName ? metaByName.get(featureName) : undefined}
            onClick={featureName ? () => onFeatureClick(featureName) : undefined}
          />
        );
      })}
    </div>
  );
};

interface HighlightCardViewProps {
  card: HighlightCard;
  metaByName: Map<string, InsightFeatureMeta>;
  meta?: InsightFeatureMeta;
  onClick?: () => void;
}
const HighlightCardView = ({ card, metaByName, meta, onClick }: HighlightCardViewProps) => (
  <CardShell onClick={onClick}>
    <div className='model-highlights-card-top'>
      <span className='model-highlights-card-title'>
        {meta && (
          // Cards are always about one feature, so the logo ignores the page's group-by.
          <ChannelIcon
            channel={meta.row.channel}
            platform={meta.row.platform}
            level={HierarchyLevelConst.BRAND_NONBRAND}
          />
        )}
        <span className='model-highlights-card-title-text'>{card.title}</span>
        {meta && (
          <MediaTypeBadge mediaType={meta.row.mediaType} variant={MediaTypeBadgeVariant.SHORT} />
        )}
        {meta?.row.incrementalityTest && <IncrementalityBeaker size={14} />}
      </span>
      <CardContext card={card} metaByName={metaByName} />
    </div>
    <div className='model-highlights-card-headline'>{card.headline}</div>
    {(card.chip || card.suggestion) && (
      // Pinned to the bottom-left corner, whatever the card's height.
      <div className='model-highlights-card-chips'>
        {card.chip && <ChipBadge chip={card.chip} />}
        {card.suggestion && (
          <Badge size={Size.SM} appearance={Appearance.OUTLINED}>
            {card.suggestion}
          </Badge>
        )}
      </div>
    )}
  </CardShell>
);

interface CardContextProps {
  card: HighlightCard;
  metaByName: Map<string, InsightFeatureMeta>;
}
// Feature names listed in the context get the same beaker as a channel card's title.
const CardContext = ({ card, metaByName }: CardContextProps) => (
  <span className='model-highlights-card-context'>
    {card.contextFeatures
      ? card.contextFeatures.map((name, i) => {
          const meta = metaByName.get(name);
          return (
            <Fragment key={name}>
              {i > 0 && ' | '}
              {meta?.displayName ?? name}
              {meta?.row.incrementalityTest && <IncrementalityBeaker size={14} />}
            </Fragment>
          );
        })
      : card.context}
  </span>
);

interface ChipBadgeProps {
  chip: HighlightChip;
}
const ChipBadge = ({ chip }: ChipBadgeProps) => (
  <Badge size={Size.SM} design={TAG_BADGE_DESIGNS[chip.tone]}>
    {chip.label}
  </Badge>
);

interface CardShellProps {
  onClick: (() => void) | undefined;
  children: ReactNode;
}
// Channel cards open the drawer; portfolio cards are read-only.
const CardShell = ({ onClick, children }: CardShellProps) => {
  if (!onClick) return <div className='model-highlights-card'>{children}</div>;
  return (
    <InteractiveDiv className='model-highlights-card clickable' onClick={onClick}>
      {children}
    </InteractiveDiv>
  );
};
